/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { Formik, Form, Field } from "formik";

import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

// @material-ui/core
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";

// @material-ui/icons
import LockS from "@material-ui/icons/Lock";
import LockO from "@material-ui/icons/LockOutlined";

import { changePassword } from "./authCognito.jsx";
import {
  chgPWValidation,
  FormTextField,
  closeButton,
  btnFormSubmit,
  passwordEyeIcon,
} from "components/Form/FormField";
import { Modal } from "components/Modal/Modal";
import {
  sProceed,
  sChgPW,
  sChgPWMsg,
  sResetOk,
  sChgOK,
  sResetFail,
  sErrTwoPW,
  sOldPWWrong,
} from "assets/jss/views/ResStrings";
import { fnChgPW, idSubmit, idEyeIcon } from "assets/jss/views/ElementIDs";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface FormValues {
  oldPass: string;
  newPass1: string;
  newPass2: string;
}

interface Props extends WithStyles<typeof styles> {
  // submit: (values: FormValues) => Promise<FormikErrors<FormValues>>;
  email: string;
  onExit: () => void;
}

const Base: React.FC<Props> = ({ email, onExit, classes }: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  const showMessage = (
    title: string,
    body: string,
    warning: boolean = false,
    func: () => void = () => {},
  ) => {
    setAlertModal(
      <Modal
        title={title}
        color={warning ? "warning" : "secondary"}
        onExit={() => {
          setShowAlert(false);
          func && func();
        }}>
        {body}
      </Modal>,
    );
    setShowAlert(true);
  };

  const updatePassword = async (
    email: string,
    oldPass: string,
    newPass: string,
  ) => {
    const { result, err } = await changePassword(email, oldPass, newPass);
    showMessage(
      result ? sResetOk : sResetFail,
      result
        ? sChgOK
        : `${err.code === "NotAuthorizedException" ? sOldPWWrong : err.code}`,
      result === false,
      onExit, // also close this ChgPWDialog
    );
  };

  const handleSubmit = (values: FormValues) => {
    const { oldPass, newPass1, newPass2 } = values;
    if (newPass1 !== newPass2) {
      showMessage(sResetFail, sErrTwoPW, true);
    } else {
      updatePassword(email || "", oldPass, newPass1);
    }
  };

  const renderPasswordField = (valueName: string, labelPrefix: string) => (
    <Field
      name={valueName}
      type={showPassword ? "text" : "password"}
      icon={<LockS />}
      component={FormTextField}
      label={labelPrefix + "New Password"}
      eye={passwordEyeIcon(
        showPassword,
        () => setShowPassword(!showPassword),
        idEyeIcon,
      )}
    />
  );

  const renderInput = () => (
    <>
      <Field
        name="oldPass"
        type="password"
        icon={<LockO />}
        component={FormTextField}
        label="Old Password"
      />
      {renderPasswordField("newPass1", "")}
      {renderPasswordField("newPass2", "Retype ")}
    </>
  );

  const closeDlg = () => {
    setShowPassword(false);
    onExit();
  };

  return (
    <>
      {showAlert && alertModal}
      <Dialog
        classes={{ paper: classes.alert }}
        open={true}
        fullWidth={true}
        onClose={closeDlg}>
        <DialogTitle className={classes.alertNormal} disableTypography>
          <h4>{sChgPW}</h4>
          {closeButton(classes, closeDlg)}
        </DialogTitle>
        <div className={classes.alertBody}>
          <DialogContent className={classes.modalBody}>
            <Formik
              initialValues={{ oldPass: "", newPass1: "", newPass2: "" }}
              onSubmit={(values) => handleSubmit(values)}
              validationSchema={chgPWValidation}
              render={({ isValid }) => (
                <Form name={fnChgPW}>
                  <p>{sChgPWMsg}</p>
                  {renderInput()}
                  <DialogActions
                    className={`${classes.modalFooter} ${classes.justifyContentCenter}`}>
                    {btnFormSubmit({
                      label: sProceed,
                      disabled: !isValid,
                      id: idSubmit,
                    })}
                  </DialogActions>
                </Form>
              )}
            />
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
};

export const ChgPWDialog = withStyles(styles)(Base);

export const ChgPWDialogTest = withStyles(styles)(Base);
